import {
  fetchEnrollments,
  fetchEvents,
  fetchMovements,
  fetchPackages,
  fetchProducts,
  fetchSeasons,
} from "../lib/api.js";
import { sum } from "../lib/aggregate.js";
import { eventProfit } from "../lib/calc.js";
import { exportToExcel, printElement } from "../lib/export.js";
import { coloniaBackupSheets } from "./colonia/backup.js";
import { formatCurrency, formatDate, monthLabel, toLocalISODate } from "../lib/format.js";
import { escapeHtml, showError } from "../lib/ui.js";

/**
 * Reportes para compartir con la familia: un resumen del período que se puede
 * mandar por mensaje o guardar como PDF, más el año completo con comparación
 * contra el anterior. Pantalla exclusiva de admin.
 */
/** Días entre la venta y el evento. */
function leadDays(e) {
  return Math.round(
    (new Date(`${e.date}T00:00:00`) - new Date(`${e.sale_date}T00:00:00`)) / 86400000
  );
}

export async function renderReports(container) {
  container.innerHTML = `<h2>Reportes</h2><p>Cargando…</p>`;

  try {
    const [events, movements, products, packages, seasons] = await Promise.all([
      fetchEvents(),
      fetchMovements(),
      fetchProducts(),
      fetchPackages(),
      fetchSeasons(),
    ]);

    const now = new Date();
    let period = "mes"; // mes | trimestre | anio
    let year = now.getFullYear();
    let month = now.getMonth() + 1;
    let quarter = Math.floor(now.getMonth() / 3) + 1;

    const draw = () => {
      const range = rangeFor(period, { year, month, quarter });
      const inRange = (d) => d >= range.from && d <= range.to;

      const periodEvents = events.filter((e) => inRange(e.date));
      const periodMovements = movements.filter((m) => inRange(m.date));

      // Vendidos en el período, que no son los mismos que los realizados: sirve
      // para mirar la venta (conversión, performance) en vez de la ejecución.
      const soldInPeriod = events.filter((e) => e.sale_date && inRange(e.sale_date));

      // Ventas del período, ordenadas por cuándo se vendieron.
      const ventasDelPeriodo = [...soldInPeriod].sort((a, b) =>
        a.sale_date < b.sale_date ? 1 : -1
      );

      // Historial de ventas mes a mes, independiente del período elegido:
      // sirve para mirar la evolución, no sólo el mes actual.
      const ventasPorMes = [...
        events
          .filter((e) => e.sale_date)
          .reduce((map, e) => {
            const key = e.sale_date.slice(0, 7);
            const acc = map.get(key) ?? { mes: key, cantidad: 0, monto: 0 };
            acc.cantidad += 1;
            acc.monto += Number(e.income) || 0;
            return map.set(key, acc);
          }, new Map())
          .values()
      ].sort((a, b) => (a.mes < b.mes ? 1 : -1));

      const sinFechaDeVenta = events.filter((e) => !e.sale_date).length;

      // Anticipación real, sobre los eventos que tienen fecha de venta.
      const withLead = events.filter((e) => e.sale_date && e.date);
      const leads = withLead.map((e) => leadDays(e));
      const avgLead = leads.length ? Math.round(leads.reduce((a, b) => a + b, 0) / leads.length) : null;
      const sinFecha = events.length - withLead.length;

      const income = sum(
        periodMovements.filter((m) => m.type === "ingreso"),
        (m) => m.amount
      );
      const expenses = sum(
        periodMovements.filter((m) => m.type === "egreso"),
        (m) => m.amount
      );

      const byProduct = products
        .map((p) => {
          const own = periodEvents.filter((e) => e.product_id === p.id);
          return {
            name: p.name,
            color: p.color,
            events: own.length,
            income: sum(own, (e) => e.income),
            profit: sum(own, eventProfit),
          };
        })
        .filter((p) => p.events > 0)
        .sort((a, b) => b.profit - a.profit);

      const comparison = comparePeriods(events, period, { year, month, quarter });

      container.innerHTML = `
        <div id="view">
          <h2>Reportes</h2>

          <div class="filters no-print">
            <select id="r-period">
              <option value="mes"${period === "mes" ? " selected" : ""}>Mensual</option>
              <option value="trimestre"${period === "trimestre" ? " selected" : ""}>Trimestral</option>
              <option value="anio"${period === "anio" ? " selected" : ""}>Anual</option>
            </select>
            ${
              period === "mes"
                ? `<select id="r-month">
                    ${Array.from({ length: 12 }, (_, i) => i + 1)
                      .map((m) => `<option value="${m}"${m === month ? " selected" : ""}>${monthLabel(year, m)}</option>`)
                      .join("")}
                  </select>`
                : ""
            }
            ${
              period === "trimestre"
                ? `<select id="r-quarter">
                    ${[1, 2, 3, 4]
                      .map((q) => `<option value="${q}"${q === quarter ? " selected" : ""}>${q}º trimestre</option>`)
                      .join("")}
                  </select>`
                : ""
            }
            <select id="r-year">
              ${yearsAvailable(events, now.getFullYear())
                .map((y) => `<option value="${y}"${y === year ? " selected" : ""}>${y}</option>`)
                .join("")}
            </select>
            <button class="icon-btn" id="print-report">Guardar como PDF</button>
            <button class="icon-btn" id="excel-report">Descargar Excel</button>
            <button class="icon-btn" id="backup">Backup completo</button>
          </div>

          <div id="report-sheet" class="report-sheet">
            <div class="report-header">
              <h3>Grow Up Deportes y Recreación · ${escapeHtml(range.label)}</h3>
              <p class="hint">Generado el ${formatDate(toLocalISODate())}</p>
            </div>

            <div class="cards-grid">
              <div class="card"><div class="label">Ingresos</div><div class="value positive">${formatCurrency(income)}</div></div>
              <div class="card"><div class="label">Egresos</div><div class="value negative">${formatCurrency(expenses)}</div></div>
              <div class="card">
                <div class="label">Resultado</div>
                <div class="value ${income - expenses >= 0 ? "positive" : "negative"}">${formatCurrency(income - expenses)}</div>
              </div>
              <div class="card"><div class="label">Eventos</div><div class="value">${periodEvents.length}</div></div>
            </div>

            <div class="panel">
              <h3>Venta y ejecución</h3>
              <div class="cards-grid">
                <div class="card">
                  <div class="label">Realizados en el período</div>
                  <div class="value">${periodEvents.length}</div>
                </div>
                <div class="card">
                  <div class="label">Vendidos en el período</div>
                  <div class="value">${soldInPeriod.length}</div>
                </div>
                <div class="card">
                  <div class="label">Anticipación promedio</div>
                  <div class="value">${avgLead === null ? "—" : `${avgLead} días`}</div>
                </div>
              </div>
              <p class="hint">
                ${
                  avgLead === null
                    ? "Todavía no hay eventos con fecha de venta cargada, así que no se puede medir la anticipación."
                    : `Medido sobre ${withLead.length} evento(s) con fecha de venta.` +
                      (sinFecha > 0 ? ` Quedan ${sinFecha} sin esa fecha, y no entran en el promedio.` : "")
                }
              </p>
            </div>

            <div class="panel">
              <h3>Registro de ventas</h3>
              ${
                ventasPorMes.length === 0
                  ? `<p>Todavía no hay ventas con fecha cargada.</p>`
                  : `<div class="table-wrap"><table class="table-cards">
                      <thead><tr><th>Mes de venta</th><th>Ventas</th><th>Monto vendido</th></tr></thead>
                      <tbody>
                        ${ventasPorMes
                          .map(
                            (v) => `<tr>
                              <td data-label="Mes de venta">${monthLabel(Number(v.mes.slice(0, 4)), Number(v.mes.slice(5, 7)))}</td>
                              <td data-label="Ventas">${v.cantidad}</td>
                              <td data-label="Monto vendido">${formatCurrency(v.monto)}</td>
                            </tr>`
                          )
                          .join("")}
                      </tbody>
                    </table></div>`
              }
              ${
                sinFechaDeVenta > 0
                  ? `<p class="hint">${sinFechaDeVenta} evento(s) no tienen fecha de venta cargada y no aparecen acá.</p>`
                  : ""
              }
            </div>

            <div class="panel">
              <h3>Ventas de ${escapeHtml(range.label)}</h3>
              ${
                ventasDelPeriodo.length === 0
                  ? `<p>No se vendió nada en este período.</p>`
                  : `<div class="table-wrap"><table class="table-cards">
                      <thead><tr><th>Vendido</th><th>Evento</th><th>Paquete</th><th>Monto</th><th>Se hace</th></tr></thead>
                      <tbody>
                        ${ventasDelPeriodo
                          .map(
                            (e) => `<tr>
                              <td data-label="Vendido">${formatDate(e.sale_date)}</td>
                              <td data-label="Evento">${escapeHtml(e.description)}</td>
                              <td data-label="Paquete">${escapeHtml(packages.find((p) => p.id === e.package_id)?.name ?? "—")}</td>
                              <td data-label="Monto">${formatCurrency(e.income)}</td>
                              <td data-label="Se hace">${formatDate(e.date)}</td>
                            </tr>`
                          )
                          .join("")}
                      </tbody>
                    </table></div>`
              }
            </div>

            <div class="panel">
              <h3>Por producto</h3>
              ${
                byProduct.length === 0
                  ? "<p>No hubo eventos en este período.</p>"
                  : `<div class="table-wrap"><table class="table-cards">
                      <thead><tr><th>Producto</th><th>Eventos</th><th>Ingresos</th><th>Ganancia</th></tr></thead>
                      <tbody>
                        ${byProduct
                          .map(
                            (p) => `<tr>
                              <td data-label="Producto"><span class="badge" style="background: ${escapeHtml(p.color)}">${escapeHtml(p.name)}</span></td>
                              <td data-label="Eventos">${p.events}</td>
                              <td data-label="Ingresos">${formatCurrency(p.income)}</td>
                              <td data-label="Ganancia" class="${p.profit >= 0 ? "value positive" : "value negative"}" style="font-size: 14px">${formatCurrency(p.profit)}</td>
                            </tr>`
                          )
                          .join("")}
                      </tbody>
                    </table></div>`
              }
            </div>

            <div class="panel">
              <h3>Comparación con ${escapeHtml(comparison.label)}</h3>
              <div class="table-wrap"><table class="table-cards">
                <thead><tr><th></th><th>${escapeHtml(comparison.label)}</th><th>${escapeHtml(range.label)}</th><th>Variación</th></tr></thead>
                <tbody>
                  ${comparisonRow("Eventos", comparison.previous.events, comparison.current.events, false)}
                  ${comparisonRow("Ingresos", comparison.previous.income, comparison.current.income, true)}
                  ${comparisonRow("Ganancia", comparison.previous.profit, comparison.current.profit, true)}
                </tbody>
              </table></div>
            </div>

            ${seasonsPanel(seasons, range)}
          </div>
        </div>
      `;

      const view = container.querySelector("#view");

      view.querySelector("#r-period").addEventListener("change", (e) => {
        period = e.target.value;
        draw();
      });
      view.querySelector("#r-year").addEventListener("change", (e) => {
        year = Number(e.target.value);
        draw();
      });
      view.querySelector("#r-month")?.addEventListener("change", (e) => {
        month = Number(e.target.value);
        draw();
      });
      view.querySelector("#r-quarter")?.addEventListener("change", (e) => {
        quarter = Number(e.target.value);
        draw();
      });

      view.querySelector("#print-report").addEventListener("click", () => {
        printElement(view.querySelector("#report-sheet"), `Grow Up ${range.label}`);
      });

      view.querySelector("#excel-report").addEventListener("click", () => {
        exportToExcel(
          {
            Resumen: [
              { Concepto: "Período", Valor: range.label },
              { Concepto: "Ingresos", Valor: income },
              { Concepto: "Egresos", Valor: expenses },
              { Concepto: "Resultado", Valor: income - expenses },
              { Concepto: "Eventos", Valor: periodEvents.length },
            ],
            "Por producto": byProduct.map((p) => ({
              Producto: p.name,
              Eventos: p.events,
              Ingresos: p.income,
              Ganancia: p.profit,
            })),
            Eventos: periodEvents.map((e) => ({
              Fecha: e.date,
              Evento: e.description,
              Producto: products.find((p) => p.id === e.product_id)?.name ?? "",
              Chicos: e.children_count,
              Ingreso: e.income,
              Costos: e.cost_total,
              Ganancia: e.profit,
            })),
            Movimientos: periodMovements.map((m) => ({
              Fecha: m.date,
              Tipo: m.type,
              Categoría: m.category,
              Descripción: m.description,
              Monto: m.amount,
              Origen: m.auto_generated ? "Automático" : "Manual",
            })),
          },
          `grow-up-${range.slug}.xlsx`
        );
      });

      view.querySelector("#backup").addEventListener("click", async () => {
        const allEnrollments = await fetchEnrollments();
        // Colonia nueva: hojas propias, sólo con el interruptor prendido.
        const colonia = await coloniaBackupSheets().catch(() => ({}));
        exportToExcel(
          {
            Eventos: events.map((e) => ({
              Fecha: e.date,
              Evento: e.description,
              Producto: products.find((p) => p.id === e.product_id)?.name ?? "",
              Chicos: e.children_count,
              Docentes: e.teachers_count,
              Souvenirs: e.souvenirs_count,
              Costos: (Array.isArray(e.cost_lines) ? e.cost_lines : [])
                .map((l) => `${l.concept}: ${l.amount}${l.per && l.per !== "fijo" ? ` (${l.per})` : ""}`)
                .join(" · "),
              Ingreso: e.income,
              Costos: e.cost_total,
              Ganancia: e.profit,
              Notas: e.notes ?? "",
            })),
            Movimientos: movements.map((m) => ({
              Fecha: m.date,
              Tipo: m.type,
              Categoría: m.category,
              Descripción: m.description,
              Monto: m.amount,
              Origen: m.auto_generated ? "Automático" : "Manual",
            })),
            Productos: products.map((p) => ({ Producto: p.name, Tipo: p.kind })),
            Temporadas: seasons.map((s) => ({
              Temporada: s.name,
              "Inscripción desde": s.enrollment_start,
              "Inscripción hasta": s.enrollment_end,
              "Colonia desde": s.season_start,
              "Colonia hasta": s.season_end,
              "Precio por chico": s.price_per_child,
            })),
            Inscripciones: allEnrollments.map((e) => ({
              Temporada: seasons.find((s) => s.id === e.season_id)?.name ?? "",
              Chico: e.child_name,
              Adulto: e.guardian_name ?? "",
              Contacto: e.contact ?? "",
              "A pagar": e.amount_due,
              Pagado: e.amount_paid,
              Saldo: Number(e.amount_due) - Number(e.amount_paid),
            })),
            ...colonia,
          },
          `grow-up-backup-${toLocalISODate()}.xlsx`
        );
      });
    };

    draw();
  } catch (error) {
    showError(container, error);
  }
}

function comparisonRow(label, previous, current, isMoney) {
  const diff = current - previous;
  const pct = previous !== 0 ? Math.round((diff / Math.abs(previous)) * 100) : null;
  const fmt = (v) => (isMoney ? formatCurrency(v) : v);

  return `<tr>
    <td data-label="">${label}</td>
    <td data-label="Antes">${fmt(previous)}</td>
    <td data-label="Ahora">${fmt(current)}</td>
    <td data-label="Variación" class="${diff >= 0 ? "value positive" : "value negative"}" style="font-size: 14px">
      ${diff >= 0 ? "+" : ""}${fmt(diff)}${pct === null ? "" : ` (${pct >= 0 ? "+" : ""}${pct}%)`}
    </td>
  </tr>`;
}

function seasonsPanel(seasons, range) {
  const inRange = seasons.filter((s) => s.season_start <= range.to && s.season_end >= range.from);
  if (inRange.length === 0) return "";

  return `<div class="panel">
    <h3>Temporadas activas en el período</h3>
    <ul>
      ${inRange
        .map(
          (s) => `<li>${escapeHtml(s.name)} — ${formatDate(s.season_start)} a ${formatDate(s.season_end)}</li>`
        )
        .join("")}
    </ul>
  </div>`;
}

function rangeFor(period, { year, month, quarter }) {
  const pad = (n) => String(n).padStart(2, "0");
  const lastDay = (y, m) => new Date(y, m, 0).getDate();

  if (period === "mes") {
    return {
      from: `${year}-${pad(month)}-01`,
      to: `${year}-${pad(month)}-${pad(lastDay(year, month))}`,
      label: monthLabel(year, month),
      slug: `${year}-${pad(month)}`,
    };
  }

  if (period === "trimestre") {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    return {
      from: `${year}-${pad(startMonth)}-01`,
      to: `${year}-${pad(endMonth)}-${pad(lastDay(year, endMonth))}`,
      label: `${quarter}º trimestre ${year}`,
      slug: `${year}-T${quarter}`,
    };
  }

  return { from: `${year}-01-01`, to: `${year}-12-31`, label: `Año ${year}`, slug: `${year}` };
}

function comparePeriods(events, period, { year, month, quarter }) {
  const previous =
    period === "mes"
      ? month === 1
        ? { year: year - 1, month: 12, quarter }
        : { year, month: month - 1, quarter }
      : period === "trimestre"
        ? quarter === 1
          ? { year: year - 1, month, quarter: 4 }
          : { year, month, quarter: quarter - 1 }
        : { year: year - 1, month, quarter };

  const currentRange = rangeFor(period, { year, month, quarter });
  const previousRange = rangeFor(period, previous);

  const slice = (range) => events.filter((e) => e.date >= range.from && e.date <= range.to);
  const stats = (list) => ({
    events: list.length,
    income: sum(list, (e) => e.income),
    profit: sum(list, eventProfit),
  });

  return {
    label: previousRange.label,
    current: stats(slice(currentRange)),
    previous: stats(slice(previousRange)),
  };
}

function yearsAvailable(events, currentYear) {
  const years = new Set(events.map((e) => Number(e.date.slice(0, 4))));
  years.add(currentYear);
  return [...years].sort((a, b) => b - a);
}
